// @ts-check

/**
 * @file Renders the organization detail route.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams, useRouter} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, View} from "react-native-propforge"
import Button from "../../../src/components/button"
import buildRoutePath from "../../../src/shared/build-route-path"
import Card from "../../../src/components/card"
import DetailValue from "../../../src/components/detail-value"
import errorMessage from "../../../src/shared/error-message"
import Link from "../../../src/components/link"
import Modal from "../../../src/components/modal"
import {requiredRouteParam} from "../../../src/shared/route-params"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import {Organization} from "../../../src/frontend-models/organization"
import ScreenTitle from "../../../src/components/screen-title"
import Text from "../../../src/components/text"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import Project from "../../../src/frontend-models/project" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} OrganizationsShowScreenProps */

/**
 * @typedef {object} OrganizationsShowScreenState
 * @property {boolean} confirmingDelete Whether delete confirmation is visible.
 * @property {boolean} loading Whether organization details are loading.
 * @property {Organization | null} organization Loaded organization record.
 * @property {Project[]} projects Loaded organization projects.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 */

/**
 * Organization show screen.
 * @augments {ShapeComponent<OrganizationsShowScreenProps, OrganizationsShowScreenState>}
 */
class OrganizationsShowScreen extends ShapeComponent {
  /** @type {boolean} */
  pendingDeleteRedirect = false
  /** @type {string} */
  organizationID
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {OrganizationsShowScreenState} */
  state = {
    confirmingDelete: false,
    loading: true,
    organization: /** @type {Organization | null} */ null,
    projects: /** @type {Project[]} */ [],
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null
  }

  /** Initializes route params and organization loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const organizationID = requiredRouteParam(params, "id", "Organization ID")
    this.organizationID = organizationID
    this.params = params
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const organization = await Organization
            .preload({projects: true})
            .find(organizationID)
          if (cancelled) return
          const projects = Array.from(organization.projectsLoaded()).sort((leftProject, rightProject) => (
            (leftProject.name() || "").localeCompare(rightProject.name() || "")
          ))
          if (cancelled) return
          this.s.loading = false
          this.s.organization = organization
          this.s.projects = projects
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [organizationID])
  }

  /** Opens delete confirmation for the organization. */
  onDeletePress = () => {
    this.s.confirmingDelete = true
    this.s.status = null
  }

  /** Closes delete confirmation. */
  onCancelDeletePress = () => {
    this.s.confirmingDelete = false
  }

  /**
   * Confirms and deletes the organization.
   * @returns {Promise<void>} Resolves when delete handling completes.
   * @rejects {unknown} When deletion fails.
   */
  onConfirmDeletePress = async () => {
    const {organization} = this.s
    if (!organization) return
    try {
      await organization.destroy()
      this.pendingDeleteRedirect = true
      this.s.confirmingDelete = false
      this.s.status = {
        kind: "success",
        text: _("The organization was deleted")
      }
      return
    } catch (error) {
      this.s.confirmingDelete = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }

  /**
   * Navigates to the organization list after the delete-confirm modal finishes
   * fading out. Replacing the route immediately would remount the screen and
   * drop the modal before it could animate.
   * @returns {void} Nothing.
   */
  onDeleteConfirmDismiss = () => {
    if (!this.pendingDeleteRedirect) return
    this.pendingDeleteRedirect = false
    this.tt.router.replace("/organizations")
  }

  /**
   * Renders organization details.
   * @returns {ReactNode} Organization detail content.
   */
  render() {
    const {confirmingDelete, loading, organization, projects, status} = this.s
    const {organizationID, params} = this.tt
    const screenTitle = organization ? organization.name() : _("Organization")
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="organizationShowScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          width: "100%"
        }}
        testID="organizationShowScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <Modal
          cardStyle={styles[`confirmModalCard-${this.tt.themeName}`] ||= {
            borderColor: this.tt.colors.danger,
            maxWidth: 420
          }}
          isVisible={confirmingDelete}
          onDismiss={this.tt.onDeleteConfirmDismiss}
          onRequestClose={this.tt.onCancelDeletePress}
          testID="organizationShowScreen/deleteConfirm"
        >
          <Text
            style={styles[`confirmTitle-${this.tt.themeName}`] ||= {
              color: this.tt.colors.dangerText,
              fontSize: 16,
              fontWeight: 700,
              marginBottom: 12
            }}
            testID="organizationShowScreen/deleteConfirm/title"
          >
            {_("Delete organization")}
          </Text>
          <View
            style={styles.confirmActions ||= {
              flexDirection: "row",
              gap: 8
            }}
            testID="organizationShowScreen/deleteConfirm/actions"
          >
            <Button
              iconName="trash"
              label={_("Delete")}
              onPress={this.tt.onConfirmDeletePress}
              testID="organizationShowScreen/deleteConfirm/confirmButton"
              variant="destructive"
            />
            <Button
              iconName="xmark"
              label={_("Cancel")}
              onPress={this.tt.onCancelDeletePress}
              testID="organizationShowScreen/deleteConfirm/cancelButton"
              variant="outline"
            />
          </View>
        </Modal>
        <View
          testID="organizationShowScreen/content"
        >
          {status &&
            <Text
              style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                color: status.kind === "error" ? this.tt.colors.dangerText : this.tt.colors.successText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="organizationShowScreen/status"
            >
              {status.text}
            </Text>
          }
          {organization &&
            <Card
              style={styles.projectsCard ||= {padding: 20}}
              testID="organizationShowScreen/projectsCard"
            >
              <Text
                style={styles[`projectsTitle-${this.tt.themeName}`] ||= {
                  fontSize: 18,
                  fontWeight: 700,
                  marginBottom: 12
                }}
                testID="organizationShowScreen/projectsTitle"
              >
                {_("Projects")}
              </Text>
              {projects.length === 0 ? (
                <Text
                  style={styles[`projectsEmpty-${this.tt.themeName}`] ||= {
                    color: this.tt.colors.textMuted
                  }}
                  testID="organizationShowScreen/projectsEmpty"
                >
                  {_("No projects yet.")}
                </Text>
              ) : (
                projects.map((project) => {
                  const projectName = project.name() || "-"

                  return (
                    <Link
                      asChild
                      href={buildRoutePath(params, `/projects/${project.id()}`)}
                      key={project.id()}
                    >
                      <Pressable
                        accessibilityLabel={projectName}
                        style={styles[`projectRow-${this.tt.themeName}`] ||= {
                          alignSelf: "stretch",
                          borderBottomColor: this.tt.colors.divider,
                          borderBottomWidth: 1,
                          paddingVertical: 10
                        }}
                        testID={`organizationShowScreen/project-${project.id()}/row`}
                      >
                        <Text
                          style={styles[`projectLinkText-${this.tt.themeName}`] ||= {
                            color: this.tt.colors.link,
                            fontSize: 16,
                            fontWeight: 600
                          }}
                          testID={`organizationShowScreen/project-${project.id()}/title/text`}
                        >
                          {projectName}
                        </Text>
                      </Pressable>
                    </Link>
                  )
                })
              )}
            </Card>
          }
          {organization &&
            <Card
              style={styles.detailsCardSpacing ||= {marginTop: 16, padding: 20}}
              testID="organizationShowScreen/card"
            >
              <View
                style={styles.headerRow ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 20
                }}
                testID="organizationShowScreen/headerRow"
              >
                <Text
                  style={styles[`headerTitle-${this.tt.themeName}`] ||= {
                    fontSize: 24,
                    fontWeight: 700
                  }}
                  testID="organizationShowScreen/title"
                >
                  {screenTitle}
                </Text>
                <View
                  style={styles.headerActions ||= {
                    flexDirection: "row",
                    gap: 8
                  }}
                  testID="organizationShowScreen/headerActions"
                >
                  <Button
                    href={buildRoutePath(params, `/organizations/${organizationID}/edit`)}
                    iconName="pen-to-square"
                    label={_("Edit")}
                    testID="organizationShowScreen/editButton"
                  />
                  <Button
                    iconName="trash"
                    label={_("Delete")}
                    onPress={this.tt.onDeletePress}
                    testID="organizationShowScreen/deleteButton"
                    variant="destructive"
                  />
                </View>
              </View>
              {detailRow({
                colors: this.tt.colors,
                label: _("Name"),
                testID: "name",
                themeName: this.tt.themeName,
                value: organization.name() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Email"),
                testID: "email",
                themeName: this.tt.themeName,
                value: organization.email() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("VAT no"),
                testID: "vatNo",
                themeName: this.tt.themeName,
                value: organization.vatNo() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Payment info"),
                testID: "paymentInfo",
                themeName: this.tt.themeName,
                value: organization.paymentInfo() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Delivery address"),
                testID: "deliveryAddress",
                themeName: this.tt.themeName,
                value: organization.deliveryAddress() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Delivery zip code"),
                testID: "deliveryZipCode",
                themeName: this.tt.themeName,
                value: organization.deliveryZipCode() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Delivery city"),
                testID: "deliveryCity",
                themeName: this.tt.themeName,
                value: organization.deliveryCity() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Delivery country"),
                testID: "deliveryCountry",
                themeName: this.tt.themeName,
                value: organization.deliveryCountry() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Invoice address"),
                testID: "invoiceAddress",
                themeName: this.tt.themeName,
                value: organization.invoiceAddress() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Invoice zip code"),
                testID: "invoiceZipCode",
                themeName: this.tt.themeName,
                value: organization.invoiceZipCode() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Invoice city"),
                testID: "invoiceCity",
                themeName: this.tt.themeName,
                value: organization.invoiceCity() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Invoice country"),
                testID: "invoiceCountry",
                themeName: this.tt.themeName,
                value: organization.invoiceCountry() || "-"
              })}
            </Card>
          }
        </View>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<OrganizationsShowScreenProps>} */
const OrganizationsShowScreenComponent = memo(shapeComponent(OrganizationsShowScreen))

export default OrganizationsShowScreenComponent

/**
 * Renders one organization detail row.
 * @param {{colors: import("../../../src/shared/theme").BoardThemeColors, label: string, testID: string, themeName: string, value: string}} args Detail row values.
 * @returns {ReactNode} Rendered metadata row.
 */
function detailRow({colors, label, testID, themeName, value}) {
  return (
    <View
      collapsable={false}
      style={styles[`detailRow-${themeName}`] ||= {
        borderBottomColor: colors.divider,
        borderBottomWidth: 1,
        paddingVertical: 10
      }}
      testID={`organizationShowScreen/${testID}/row`}
    >
      <Text
        style={styles[`detailLabel-${themeName}`] ||= {
          color: colors.textMuted,
          fontWeight: 600,
          marginBottom: 4
        }}
        testID={`organizationShowScreen/${testID}/label`}
      >
        {label}
      </Text>
      <DetailValue
        style={styles[`detailValue-${themeName}`] ||= {fontSize: 16}}
        testID={`organizationShowScreen/${testID}/value`}
        value={value}
      />
    </View>
  )
}
